import { useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import FormControlLabel from '@mui/material/FormControlLabel'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import Typography from '@mui/material/Typography'
import { inNativeApp, nativeCall } from '../lib/nativeApp'
import { fetchPushSettings, savePushSetting } from '../lib/pushPreferences'
import { pushPermissionFrom, type PushPermission, type PushSetting } from '../utilities/pushPreferences'

/**
 * Push notification settings, on the Account page of the noBGP app only (mobile/). A browser
 * receives no push, so it shows nothing.
 *
 * Two layers, and the page shows both: what the PHONE allows (the system permission, read from
 * @capacitor/push-notifications) and which categories THIS PERSON wants (the router's
 * /v1/push/preferences, the same on all their phones). The switches are only drawn once the phone
 * allows notifications, because a switch that is on and silent is the worse answer.
 */
export default function NotificationSettings() {
  const [permission, setPermission] = useState<PushPermission>('unknown')
  const [settings, setSettings] = useState<PushSetting[] | null>(null)
  const [state, setState] = useState<'loading' | 'ready' | 'unavailable' | 'error'>('loading')
  const [saving, setSaving] = useState<string | null>(null)

  const readPermission = () =>
    nativeCall('PushNotifications', 'checkPermissions')
      .then(answer => setPermission(pushPermissionFrom(answer)))
      .catch(() => setPermission('unknown'))

  useEffect(() => {
    if (!inNativeApp) return
    void readPermission()
    void fetchPushSettings().then(result => {
      if (result.ok) {
        setSettings(result.settings)
        setState('ready')
      } else setState(result.reason)
    })
    // Coming back from the phone's Settings app: the permission may have changed there.
    const onVisible = () => {
      if (!document.hidden) void readPermission()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [])

  if (!inNativeApp) return null

  const turnOn = async () => {
    const answer = await nativeCall('PushNotifications', 'requestPermissions').catch(() => null)
    const next = pushPermissionFrom(answer)
    setPermission(next)
    // The app's shell (nobgp-shell.js) registers the token with the router on `registration`.
    if (next === 'granted') await nativeCall('PushNotifications', 'register').catch(() => null)
  }

  const toggle = async (row: PushSetting, enabled: boolean) => {
    setSaving(row.id)
    setSettings(rows => rows?.map(r => (r.id === row.id ? { ...r, enabled } : r)) ?? rows)
    const result = await savePushSetting(row.id, enabled)
    if (result.ok) setSettings(result.settings)
    else setSettings(rows => rows?.map(r => (r.id === row.id ? { ...r, enabled: !enabled } : r)) ?? rows)
    setSaving(null)
  }

  return (
    <>
      <Divider />
      <Box>
        <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1.5 }}>
          Notifications
        </Typography>
        {permission === 'denied' && (
          <Stack spacing={1.5} sx={{ alignItems: 'flex-start' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Notifications are turned off for noBGP on this device.
            </Typography>
            <Button variant="outlined" size="small" onClick={() => void nativeCall('NobgpShell', 'openSettings')}>
              Open Settings
            </Button>
          </Stack>
        )}
        {permission === 'prompt' && (
          <Stack spacing={1.5} sx={{ alignItems: 'flex-start' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Get a notification when a node goes offline, someone asks for access, or a billing alert fires.
            </Typography>
            <Button variant="contained" size="small" onClick={() => void turnOn()}>
              Turn on notifications
            </Button>
          </Stack>
        )}
        {permission === 'granted' && state === 'unavailable' && (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Notifications are not available yet.
          </Typography>
        )}
        {permission === 'granted' && state === 'error' && (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Your notification settings could not be read. Open this page again to try.
          </Typography>
        )}
        {permission === 'granted' && state === 'ready' && settings && (
          <Stack spacing={1.5}>
            {settings.map(row => (
              <Box key={row.id}>
                <FormControlLabel
                  sx={{ alignItems: 'flex-start', ml: 0 }}
                  labelPlacement="end"
                  control={
                    <Switch
                      checked={row.enabled}
                      disabled={saving === row.id}
                      onChange={(_, checked) => void toggle(row, checked)}
                    />
                  }
                  label={
                    <Box sx={{ pt: 0.75 }}>
                      <Typography variant="body1">{row.label}</Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {row.detail}
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            ))}
          </Stack>
        )}
      </Box>
    </>
  )
}
